/**
 * Thin client for the generic CRUD endpoints of the hospitality-extension services.
 * Every call goes through the HMS gateway:  {HMS_API_BASE}/api/<module>/<schema>/<table>
 * The bearer token is the same central-auth token the other HMS screens send.
 */
import { hzPropertyId } from "../Api/hzApi";

/** Gateway origin. If your other HMS calls use a different origin, change only this line. */
export const HMS_API_BASE: string = "https://hms.cilpron.com";

export interface MetaColumn { name: string; type: string; nullable: boolean; key: boolean; readOnly: boolean; concurrencyToken: boolean }
export interface TableMeta { schema: string; table: string; operations: string; key: string[]; propertyScoped: boolean; columns: MetaColumn[] }
export interface PageResult { items: Record<string, unknown>[]; page: number; pageSize: number; total: number }

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) { super(message); this.status = status; }
}

const url = (route: string, tail = "") => `${HMS_API_BASE}/api/${route}${tail}`;

async function send<T>(token: string | undefined, method: string, target: string, body?: unknown): Promise<{ data: T; res: Response }> {
  const res = await fetch(target, {
    method,
    headers: {
      Accept: "application/json",
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 204) return { data: undefined as T, res };
  const text = await res.text();
  let json: any = undefined;
  try { json = text ? JSON.parse(text) : undefined; } catch { /* not json */ }
  if (!res.ok) {
    const msg = json?.detail || json?.title || (res.status === 403 ? "You do not have permission for this." : res.status === 401 ? "Please sign in again." : res.status === 404 ? "Not found. Check that this module is deployed." : text || `Request failed (${res.status})`);
    throw new ApiError(res.status, String(msg));
  }
  return { data: json as T, res };
}

export const loadMeta = (token: string | undefined, route: string) => send<TableMeta>(token, "GET", url(route, "/meta")).then((r) => r.data);

export function loadPage(token: string | undefined, route: string, q: { page: number; pageSize: number; sort?: string; desc?: boolean; filters?: Record<string, string> }) {
  const p = new URLSearchParams({ page: String(q.page), pageSize: String(q.pageSize) });
  if (q.sort) { p.set("sort", q.sort); if (q.desc) p.set("desc", "true"); }
  Object.entries(q.filters ?? {}).forEach(([k, v]) => { if (v !== "") p.set(k, v); });
  return send<PageResult>(token, "GET", url(route, `?${p.toString()}`)).then((r) => r.data);
}

export const createRow = (token: string | undefined, route: string, body: Record<string, unknown>) => send<Record<string, unknown>>(token, "POST", url(route), body).then((r) => r.data);
export const patchRow = (token: string | undefined, route: string, key: string, body: Record<string, unknown>) => send<Record<string, unknown>>(token, "PATCH", url(route, `/${encodeURIComponent(key)}`), body).then((r) => r.data);
export const deleteRow = (token: string | undefined, route: string, key: string) => send<void>(token, "DELETE", url(route, `/${encodeURIComponent(key)}`)).then(() => undefined);

/** Case-insensitive property read: the API returns camelCase, the metadata returns the C# names. */
export function cell(row: Record<string, unknown>, name: string): unknown {
  if (name in row) return row[name];
  const l = name.toLowerCase();
  for (const k of Object.keys(row)) if (k.toLowerCase() === l) return row[k];
  return undefined;
}

/** The property the user is working in, or null when the app has not chosen one yet. */
export function currentPropertyId(): number | null {
  try {
    const v: any = hzPropertyId as any;
    const n = Number(typeof v === "function" ? v() : v);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch { return null; }
}
